import React, { useState, createRef } from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  Badge,
  HStack,
  Tooltip,
  keyframes,
  Icon,
  SimpleGrid,
  Flex,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  FormControl,
  FormLabel,
  Input,
} from "@chakra-ui/react";
import { InfoIcon, TimeIcon, CheckIcon, CloseIcon, ChevronLeftIcon, ChevronRightIcon, LockIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import FullCalendar from "@fullcalendar/react";
import { EventInput, EventContentArg } from "@fullcalendar/core";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import dayGridPlugin from "@fullcalendar/daygrid";
import { Link } from "react-router-dom";

import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { gql, useQuery, useMutation } from "@apollo/client";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { LoginWithSmsModal } from "../../components/LoginWithSmsModal";
import { CaptureUserDetailsModal } from "../../components/CaptureUserDetailsModal";
import { brandConfig } from "../../brandConfig";

// GraphQL Queries and Mutations
const GET_PRACTITIONER_AVAILABILITY = gql`
  query GetAvailabilityByPractitionerId($practitionerId: ID!, $startDate: String!, $endDate: String!) {
    getAvailabilityByPractitionerId(
      practitionerId: $practitionerId
      startDate: $startDate
      endDate: $endDate
    ) {
      id
      practitioner {
        id
      }
      startTime
      endTime
      isAvailable
      status
    }
  }
`;

// Add query to get all practitioner bookings
const GET_PRACTITIONER_BOOKINGS = gql`
  query GetPractitionerBookings($practitionerId: String!) {
    getBookingsByPractitionerId(practitionerId: $practitionerId) {
      id
      bookingTime
      status
      startTime
      endTime
      practitionerId
      client {
        id
        fName
        lName
      }
      sessionType {
        id
        name
        format
      }
    }
  }
`;

// Add query to get practitioner details
const GET_PRACTITIONER_BY_ID = gql`
  query PractitionerById($id: ID!) {
    practionerById(id: $id) {
      id
      fName
      lName
      email
      phoneNumber
      businessName
    }
  }
`;

const GET_SESSION_TYPES_BY_PRACTITIONER = gql`
  query GetSessionTypesByPractitioner($practitionerId: ID!) {
    sessionTypesByPractitioner(practitionerId: $practitionerId) {
      id
      name
      description
      format
      duration
      price
      color
      availableOnCalendar
      clientId
      practitionerId
      meetingLink
      tenantId
      createdAt
      updatedAt
    }
  }
`;

const GET_CLIENT_BOOKINGS = gql`
  query GetClientBookings($clientId: String!) {
    bookingsByClientId(clientId: $clientId) {
      id
      bookingTime
      status
      startTime
      endTime
      payment {
        status
        method
        amount
        currency
      }
      sessionType {
        id
        name
        format
        duration
        price
      }
      practitionerId
      sendReminders
    }
  }
`;

// const CREATE_BOOKING = gql`
//   mutation CreateDirectBooking($input: BookingInput!) {
//     createDirectBooking(input: $input) {
//       id
//       bookingTime
//       status
//       payment {
//         status
//         method
//         amount
//       }
//       client {
//         id
//         fName
//         lName
//       }
//       startTime
//       endTime
//       sessionType {
//         id
//         name
//         duration
//         price
//       }
//     }
//   }
// `;


const CREATE_BOOKING = gql`
  mutation CreateDirectBooking($input: BookingInput!) {
    createDirectBooking(input: $input) {
      id
      bookingTime
      status
      payment {
        status
        method
        amount
      }
      client {
        id
        fName
        lName
      }
      startTime
      endTime
      sessionType {
        id
        name
        duration
        price
      }
    }
  }
`;

const CANCEL_BOOKING = gql`
  mutation CancelBooking($bookingId: String!, $reason: String!) {
    cancelBooking(bookingId: $bookingId, reason: $reason) {
      id
      status
      cancellationReason
      startTime
      endTime
      sessionType {
        name
      }
    }
  }
`;

// Define BookingPaymentMethod enum
enum BookingPaymentMethod {
  STRIPE = "STRIPE",
  BANK_TRANSFER = "BANK_TRANSFER",
  CRYPTO = "CRYPTO",
  OTHER = "OTHER"
}

// UI Animations
const shimmerAnimation = keyframes`
  0% { background-position: -500px 0; }
  100% { background-position: 500px 0; }
`;

const glowAnimation = keyframes`
  0%, 100% { box-shadow: 0 0 5px rgba(57, 161, 105, 0.5); }
  50% { box-shadow: 0 0 20px rgba(57, 161, 105, 0.8); }
`;

// Add spin animation for loading spinner
const spinAnimation = keyframes`
  0% { transform: translate(-50%, -50%) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(360deg); }
`;

export const ClientBookingCalendarByPractitionerId = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { practitionerId } = useParams<{ practitionerId: string }>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isLoginModalOpen,
    onOpen: onLoginModalOpen,
    onClose: onLoginModalClose
  } = useDisclosure();
  
  const { 
    isOpen: isBookingDetailOpen, 
    onOpen: onBookingDetailOpen, 
    onClose: onBookingDetailClose 
  } = useDisclosure();
  
  const {
    isOpen: isUserDetailsModalOpen,
    onOpen: onUserDetailsModalOpen,
    onClose: onUserDetailsModalClose
  } = useDisclosure();
  
  const [selectedSlot, setSelectedSlot] = useState<{ start: Date; end: Date } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedSessionType, setSelectedSessionType] = useState<any>(null);
  const [is24HourView, setIs24HourView] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [cancellationReason, setCancellationReason] = useState("");
  const [pendingSlotSelection, setPendingSlotSelection] = useState<{ start: Date; end: Date } | null>(null);
  
  // Get authenticated user from context
  const { user, isAuthenticated, loading: authLoading, refreshAuth } = useAuth();

  // Calculate date range for query based on calendar view
  const [queryDateRange, setQueryDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString(),
    endDate: new Date(new Date().setDate(new Date().getDate() + 21)).toISOString()
  });

  // Query for practitioner details
  const { data: practitionerData, loading: practitionerLoading } = useQuery(GET_PRACTITIONER_BY_ID, {
    variables: {
      id: practitionerId || ""
    },
    skip: !practitionerId,
    onCompleted: (data) => {
      console.log("Practitioner data received:", data);
    },
    onError: (error) => {
      console.error("Error fetching practitioner details:", error);
    }
  });

  // Query for session types
  const { data: sessionTypesData, loading: sessionTypesLoading } = useQuery(GET_SESSION_TYPES_BY_PRACTITIONER, {
    variables: {
      practitionerId: practitionerId || ""
    },
    skip: !practitionerId,
    onCompleted: (data) => {
      console.log("Session types data received:", data);
    },
    onError: (error) => {
      console.error("Error fetching session types:", error);
    }
  });

  // Query for practitioner availabilities
  const { data: availabilityData, loading: availabilityLoading, refetch: refetchAvailability } = useQuery(GET_PRACTITIONER_AVAILABILITY, {
    variables: {
      practitionerId: practitionerId || "",
      startDate: queryDateRange.startDate,
      endDate: queryDateRange.endDate
    },
    skip: !practitionerId,
    onCompleted: (data) => {
      console.log("Practitioner availability data received:", {
        dataReceived: data,
        availabilityCount: data?.getAvailabilityByPractitionerId?.length || 0,
        practitionerId: practitionerId,
        dateRange: queryDateRange,
        firstSlot: data?.getAvailabilityByPractitionerId?.[0] || "No slots found"
      });
    },
    onError: (error) => {
      console.error("Error fetching practitioner availability:", {
        error,
        message: error.message,
        practitionerId,
        dateRange: queryDateRange
      });
    }
  });

  // Add query for all practitioner bookings
  const { data: practitionerBookingsData, loading: practitionerBookingsLoading } = useQuery(GET_PRACTITIONER_BOOKINGS, {
    variables: {
      practitionerId: practitionerId || ""
    },
    skip: !practitionerId,
    onCompleted: (data) => {
      console.log("Practitioner bookings received:", data);
      
      // Additional debugging for the raw data structure
      if (data?.getBookingsByPractitionerId?.length > 0) {
        const exampleBooking = data.getBookingsByPractitionerId[0];
        console.log("Example practitioner booking object structure:", {
          rawBooking: JSON.stringify(exampleBooking),
          id: exampleBooking.id, 
          bookingTime: exampleBooking.bookingTime,
          startTime: exampleBooking.startTime,
          endTime: exampleBooking.endTime,
          status: exampleBooking.status,
          clientInfo: exampleBooking.client,
          practitionerId: exampleBooking.practitionerId || "Not in booking object",
          sessionType: exampleBooking.sessionType
        });
      }
      
      if (data?.getBookingsByPractitionerId) {
        console.log(`Found ${data.getBookingsByPractitionerId.length} practitioner bookings`);
        data.getBookingsByPractitionerId.forEach((booking: any, index: number) => {
          console.log(`Practitioner booking ${index + 1}:`, { 
            id: booking.id,
            status: booking.status,
            startTime: booking.startTime,
            endTime: booking.endTime,
            clientId: booking.client?.id || "No client",
            sessionType: booking.sessionType?.name || "N/A",
            rawPractitionerId: booking.practitionerId // This will be useful for debugging
          });
        });
      } else {
        console.log("No practitioner bookings found in the response data");
      }
    },
    onError: (error: any) => {
      console.error("Error fetching practitioner bookings:", error);
    }
  });

  // Scroll to top on component mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check if user details are complete
  React.useEffect(() => {
    if (isAuthenticated && user && 
        (!user.fName || !user.lName || !user.email)) {
      console.log("User details incomplete, showing details modal:", {
        hasFirstName: !!user.fName,
        hasLastName: !!user.lName,
        hasEmail: !!user.email
      });
      onUserDetailsModalOpen();
    }
  }, [isAuthenticated, user, onUserDetailsModalOpen]);

  // Add query for client bookings
  const { data: bookingsData, loading: bookingsLoading, refetch: refetchBookings } = useQuery(GET_CLIENT_BOOKINGS, {
    variables: {
      clientId: user?.id || ""
    },
    skip: !user?.id, // Skip the query when user is not authenticated
    onCompleted: (data) => {
      console.log("Client bookings received:", data);
      if (data?.bookingsByClientId) {
        console.log(`Found ${data.bookingsByClientId.length} bookings for client ${user?.id}`);
        data.bookingsByClientId.forEach((booking: any, index: number) => {
          console.log(`Booking ${index + 1}:`, { 
            id: booking.id,
            practitionerId: booking.practitionerId,
            status: booking.status,
            startTime: booking.startTime,
            endTime: booking.endTime,
            sessionType: booking.sessionType?.name || "N/A"
          });
        });
      } else {
        console.log("No bookings found in the response data");
      }
    },
    onError: (error) => {
      console.error("Error fetching client bookings:", error);
    }
  });

  // Mutation for creating bookings
  const [createBooking, { loading: bookingLoading }] = useMutation(CREATE_BOOKING, {
    onCompleted: (data) => {
      toast({
        title: "Booking successful!",
        description: "Your session has been booked.",
        status: "success",
        duration: 3000
      });
      refetchBookings(); // Refetch bookings to update the list
      onClose();
    },
    onError: (error) => {
      console.error("Error creating booking:", error);
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000
      });
    }
  });

  // Add cancel booking mutation
  const [cancelBooking, { loading: cancelLoading }] = useMutation(CANCEL_BOOKING, {
    onCompleted: () => {
      toast({
        title: "Booking cancelled",
        description: "Your booking has been successfully cancelled.",
        status: "success",
        duration: 3000
      });
      refetchBookings();
      onBookingDetailClose();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000
      });
    }
  });

  // Handle calendar date changes
  const handleDatesSet = (dateInfo: any) => {
    const weekStart = dateInfo.start;
    
    setSearchParams({ 
      week: weekStart.toISOString(),
      view: "timeGridWeek"
    });
    
    setQueryDateRange({
      startDate: dateInfo.start.toISOString(),
      endDate: dateInfo.end.toISOString()
    });
  };

  // Get initial calendar date from URL or use current week
  const initialDate = React.useMemo(() => {
    const weekParam = searchParams.get("week");
    
    try {
      if (weekParam) {
        const date = new Date(weekParam);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    } catch (error) {
      console.error("Error parsing date from URL:", error);
    }

    const today = new Date();
    const currentWeekStart = new Date(today);
    currentWeekStart.setHours(0, 0, 0, 0);
    currentWeekStart.setDate(today.getDate() - today.getDay());
    
    setSearchParams({ week: currentWeekStart.toISOString(), view: "timeGridWeek" });
    
    return currentWeekStart;
  }, [searchParams, setSearchParams]);

  // Add function to handle booking event click
  const handleEventClick = (clickInfo: any) => {
    // Check if this is a booking event
    if (clickInfo.event.extendedProps.isBooking) {
      // For viewing bookings, need to be logged in
      if (!isAuthenticated) {
        toast({
          title: "Login Required",
          description: "Please log in to view your bookings",
          status: "info",
          duration: 3000,
        });
        onLoginModalOpen();
        return;
      }
      
      const bookingId = clickInfo.event.extendedProps.bookingId;
      const booking = bookingsData?.bookingsByClientId.find((b: any) => b.id === bookingId);
      
      if (booking) {
        setSelectedBooking(booking);
        onBookingDetailOpen();
      }
      return;
    }
  };

  // Update the handleDateClick function to store the selection
  const handleDateClick = (arg: any) => {
    const clickedDate = arg.date;
    const clickedTime = new Date(clickedDate);
    const currentTime = new Date();
    
    // Prevent booking sessions in the past
    if (clickedTime < currentTime) {
      toast({
        title: "Cannot book past sessions",
        description: "You cannot book sessions in the past. Please select a future time slot.",
        status: "warning",
        duration: 3000
      });
      return;
    }
    
    // Check if this time overlaps with any other client booking
    let isOverlappingWithOtherBooking = false;
    
    // Only check for overlaps if we have practitioner booking data
    if (practitionerBookingsData?.getBookingsByPractitionerId) {
      // Get client booking IDs for quick lookup
      const clientBookingIdSet = new Set<string>();
      if (isAuthenticated && bookingsData?.bookingsByClientId) {
        bookingsData.bookingsByClientId.forEach((booking: any) => {
          clientBookingIdSet.add(booking.id);
        });
      }
      
      // Check each practitioner booking for overlap
      for (const booking of practitionerBookingsData.getBookingsByPractitionerId) {
        // Skip if no valid time data
        if (!booking.startTime || !booking.endTime) continue;
        
        // Skip client's own bookings
        if (clientBookingIdSet.has(booking.id)) continue;
        
        const bookingStart = new Date(booking.startTime);
        const bookingEnd = new Date(booking.endTime);
        
        // Check if clickedTime falls within this booking
        if (clickedTime >= bookingStart && clickedTime < bookingEnd) {
          isOverlappingWithOtherBooking = true;
          break;
        }
      }
    }
    
    // If this time is already booked by another client, prevent selection
    if (isOverlappingWithOtherBooking) {
      toast({
        title: "Time not available",
        description: "This time slot is already booked",
        status: "warning",
        duration: 3000
      });
      return;
    }
    
    // Only allow selection if a session type is selected
    if (!selectedSessionType) {
      toast({
        title: "Please select a session type",
        description: "Select a session type above before booking a time slot",
        status: "info",
        duration: 3000
      });
      return;
    }

    // Check if user is authenticated
    if (!isAuthenticated) {
      // Store the current selection for after login
      setPendingSlotSelection({
        start: clickedTime,
        end: new Date(clickedTime.getTime() + (selectedSessionType.duration * 60000))
      });

      toast({
        title: "Login Required",
        description: "Please log in to book this session",
        status: "info",
        duration: 3000,
      });
      onLoginModalOpen();
      return;
    }

    // Check if this time is within any availability window (only if availability data exists)
    const hasAvailabilityData = availabilityData?.getAvailabilityByPractitionerId?.length > 0;
    
    if (hasAvailabilityData) {
      const isWithinAvailability = availabilityData?.getAvailabilityByPractitionerId?.some((slot: any) => {
        const slotStart = new Date(slot.startTime);
        const slotEnd = new Date(slot.endTime);
        return clickedTime >= slotStart && clickedTime <= slotEnd;
      });

      if (!isWithinAvailability) {
        toast({
          title: "Time not available",
          description: "Please select a time within the available slots (green areas)",
          status: "warning",
          duration: 3000
        });
        return;
      }
    }

    // Calculate the end time based on session duration
    const sessionStart = clickedTime;
    const sessionEnd = new Date(sessionStart.getTime() + (selectedSessionType.duration * 60000));
    
    // Check if the full session duration fits within an availability slot (only if availability data exists)
    if (hasAvailabilityData) {
      const sessionFitsInAvailability = availabilityData?.getAvailabilityByPractitionerId?.some((slot: any) => {
        const slotStart = new Date(slot.startTime);
        const slotEnd = new Date(slot.endTime);
        return sessionStart >= slotStart && sessionEnd <= slotEnd;
      });

      if (!sessionFitsInAvailability) {
        toast({
          title: "Session doesn't fit",
          description: `This ${selectedSessionType.duration}-minute session doesn't fit within the available time slot.`,
          status: "error",
          duration: 3000
        });
        return;
      }
    }
    
    // Check if the session duration overlaps with any other client's booking
    let sessionOverlapsWithOtherBooking = false;
    
    // Only check for overlaps if we have practitioner booking data
    if (practitionerBookingsData?.getBookingsByPractitionerId) {
      // Get client booking IDs for quick lookup
      const clientBookingIdSet = new Set<string>();
      if (isAuthenticated && bookingsData?.bookingsByClientId) {
        bookingsData.bookingsByClientId.forEach((booking: any) => {
          clientBookingIdSet.add(booking.id);
        });
      }
      
      // Check each practitioner booking for overlap
      for (const booking of practitionerBookingsData.getBookingsByPractitionerId) {
        // Skip if no valid time data
        if (!booking.startTime || !booking.endTime) continue;
        
        // Skip client's own bookings
        if (clientBookingIdSet.has(booking.id)) continue;
        
        const bookingStart = new Date(booking.startTime);
        const bookingEnd = new Date(booking.endTime);
        
        // Check for any overlap between the booking and the proposed session
        if (
          (sessionStart >= bookingStart && sessionStart < bookingEnd) || // Session start is within booking
          (sessionEnd > bookingStart && sessionEnd <= bookingEnd) || // Session end is within booking
          (sessionStart <= bookingStart && sessionEnd >= bookingEnd) // Session completely covers booking
        ) {
          sessionOverlapsWithOtherBooking = true;
          break;
        }
      }
    }
    
    if (sessionOverlapsWithOtherBooking) {
      toast({
        title: "Session overlaps with existing booking",
        description: "This time slot is partially booked and cannot accommodate your session",
        status: "warning",
        duration: 3000
      });
      return;
    }
    
    // Set the selected slot with proper start/end times
    setSelectedSlot({
      start: sessionStart,
      end: sessionEnd
    });
    
    onOpen();
  };

  // Add function to handle booking cancellation
  const handleCancelBooking = async () => {
    if (!selectedBooking || !cancellationReason.trim()) {
      toast({
        title: "Error",
        description: "Please provide a reason for cancellation",
        status: "warning",
        duration: 3000
      });
      return;
    }

    try {
      await cancelBooking({
        variables: {
          bookingId: selectedBooking.id,
          reason: cancellationReason
        },
      });
    } catch (error) {
      // Error is handled in the onError callback
    }
  };

  // Helper functions for booking display
  // Helper function to get booking color based on status
  const getBookingColor = (status: string) => {
    switch (status) {
      case "CONFIRMED":
        return "#2196F3"; // Blue
      case "COMPLETED":
        return "#4CAF50"; // Green
      case "CANCELLED":
        return "#F44336"; // Red
      case "PENDING":
        return "#FFC107"; // Amber
      default:
        return "#9E9E9E"; // Grey
    }
  };

  // Helper function to get booking border color
  const getBookingBorderColor = (status: string) => {
    switch (status) {
      case "CONFIRMED":
        return "#1976D2"; // Darker blue
      case "COMPLETED":
        return "#388E3C"; // Darker green
      case "CANCELLED":
        return "#D32F2F"; // Darker red
      case "PENDING":
        return "#FFA000"; // Darker amber
      default:
        return "#757575"; // Darker grey
    }
  };

  // Add a pulsing animation for pending status
  const pulseAnimation = keyframes`
    0% { opacity: 0.7; transform: scale(0.95); }
    50% { opacity: 1; transform: scale(1.05); }
    100% { opacity: 0.7; transform: scale(0.95); }
  `;
  
  // Get status icon based on booking status
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "CONFIRMED":
        return CheckIcon;
      case "COMPLETED":
        return CheckIcon;
      case "CANCELLED":
        return CloseIcon;
      case "PENDING":
        return TimeIcon;
      default:
        return InfoIcon;
    }
  };

  // Helper to get human-readable status message
  const getStatusMessage = (status: string, paymentStatus?: string) => {
    switch (status) {
      case "PENDING":
        return `Booking pending${paymentStatus ? ` - Payment: ${paymentStatus}` : ""}`;
      case "CONFIRMED":
        return "Booking confirmed";
      case "COMPLETED":
        return "Session completed";
      case "CANCELLED":
        return "Booking cancelled";
      default:
        return status;
    }
  };

  // Update renderEventContent to handle booking events
  const renderEventContent = (eventInfo: EventContentArg) => {
    const isMobile = window.innerWidth < 768;
    
    // If this is another client's booking (shown as gray/unavailable)
    if (eventInfo.event.extendedProps.isOtherClientBooking) {
      return (
        <Box 
          fontSize={isMobile ? "xs" : "sm"} 
          overflow="hidden" 
          width="100%"
          height="100%"
          position="relative"
          borderRadius="md"
          p={1}
          bg="gray.600"
          opacity={0.9}
          borderLeft="4px solid"
          borderColor="gray.700"
          boxShadow="md"
        >
          <Text fontWeight="bold" isTruncated color="white">{eventInfo.timeText}</Text>
          <Text isTruncated color="white">Unavailable</Text>
          
          <HStack spacing={1} mt={1}>
            <LockIcon color="gray.300" boxSize={isMobile ? 3 : 4} />
            <Badge 
              variant="solid"
              colorScheme="gray"
              fontSize={isMobile ? "2xs" : "xs"}
              px={1}
            >
              Booked
            </Badge>
          </HStack>
        </Box>
      );
    }
    
    // If this is a booking event, not an availability
    if (eventInfo.event.extendedProps.isBooking) {
      const isPending = eventInfo.event.extendedProps.status === "PENDING";
      const StatusIcon = getStatusIcon(eventInfo.event.extendedProps.status);
      
      return (
        <Box 
          fontSize={isMobile ? "xs" : "sm"} 
          overflow="hidden" 
          width="100%"
          position="relative"
          animation={isPending ? `${pulseAnimation} 2s infinite` : undefined}
          p={1}
        >
          <Text fontWeight="bold" isTruncated>{eventInfo.timeText}</Text>
          <Text isTruncated>{eventInfo.event.title}</Text>
          
          <HStack spacing={1} mt={1}>
            <Icon 
              as={StatusIcon} 
              color={
                eventInfo.event.extendedProps.status === "CONFIRMED" ? "blue.500" : 
                eventInfo.event.extendedProps.status === "COMPLETED" ? "green.500" :
                eventInfo.event.extendedProps.status === "CANCELLED" ? "red.500" : "yellow.500"
              }
              boxSize={isMobile ? 3 : 4}
            />
            
            <Badge 
              variant="solid"
              colorScheme={
                eventInfo.event.extendedProps.status === "CONFIRMED" ? "blue" : 
                eventInfo.event.extendedProps.status === "COMPLETED" ? "green" :
                eventInfo.event.extendedProps.status === "CANCELLED" ? "red" : "yellow"
              }
              fontSize={isMobile ? "2xs" : "xs"}
              px={1}
            >
              {eventInfo.event.extendedProps.status}
            </Badge>
          </HStack>
        </Box>
      );
    }
    
    // For availability events - use existing rendering
    return (
      <Box 
        fontSize={isMobile ? "xs" : "sm"} 
        overflow="hidden" 
        width="100%"
        height="100%"
        position="relative"
        borderRadius="md"
        cursor="pointer"
        transition="all 0.3s ease"
        _before={{
          content: "\"\"",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(120deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0) 70%)",
          backgroundSize: "1000px 100%",
          animation: `${shimmerAnimation} 3s infinite linear`,
          zIndex: 0
        }}
        _hover={{
          transform: "scale(1.03)",
          boxShadow: "0 0 15px rgba(57, 161, 105, 0.7)"
        }}
      >
        <Tooltip
          label={
            <VStack spacing={2} p={2} align="start">
              <Heading size="xs">Available Time Slot</Heading>
              <Text fontSize="xs" fontWeight="bold">
                {new Date(eventInfo.event.start!).toLocaleTimeString([], {hour: "2-digit", minute:"2-digit"})} - {new Date(eventInfo.event.end!).toLocaleTimeString([], {hour: "2-digit", minute:"2-digit"})}
              </Text>
              <Text fontSize="xs">Click to book this time slot</Text>
            </VStack>
          }
          placement="top"
          hasArrow
          gutter={10}
          bg="green.600"
          borderRadius="md"
        >
          <Box p={1} position="relative" zIndex={1}>
            <HStack spacing={1} mb={1}>
              <Icon 
                as={TimeIcon} 
                color="yellow.400"
                boxSize={isMobile ? 2 : 3}
              />
              <Badge 
                colorScheme="green" 
                fontSize={isMobile ? "2xs" : "xs"}
                variant="solid"
              >
                Available
              </Badge>
            </HStack>
            
            <Text 
              fontWeight="bold" 
              fontSize={isMobile ? "2xs" : "xs"} 
              color="white"
            >
              {eventInfo.timeText}
            </Text>
          </Box>
        </Tooltip>
      </Box>
    );
  };

  // Convert availabilities to calendar events - update to include bookings
  const events: EventInput[] = React.useMemo(() => {
    // Add more debugging for availability data
    console.log("Building events from availability data:", {
      hasData: !!availabilityData?.getAvailabilityByPractitionerId,
      slotCount: availabilityData?.getAvailabilityByPractitionerId?.length || 0,
      slots: availabilityData?.getAvailabilityByPractitionerId?.slice(0, 2) || []
    });

    const availabilityEvents = !availabilityData?.getAvailabilityByPractitionerId 
      ? [] 
      : availabilityData.getAvailabilityByPractitionerId.map((slot: any) => {
          console.log("Processing availability slot:", {
            id: slot.id,
            practitioner: slot.practitioner?.id || "No practitioner ID",
            startTime: slot.startTime,
            endTime: slot.endTime,
            status: slot.status
          });
          
          return {
            id: slot.id,
            start: slot.startTime,
            end: slot.endTime,
            title: "Available",
            display: "background",  // This is the key change - make them background events
            backgroundColor: "rgba(56, 161, 105, 0.2)", // Lighter green for background
            borderColor: "#2F855A",
            textColor: "#ffffff",
            editable: false,
            extendedProps: {
              isAvailability: true,
              status: slot.status,
              isBooking: false,
              practitionerId: slot.practitioner?.id
            }
          };
        });
    
    // Get client's booking IDs for comparison
    const clientBookingIds = new Set(
      isAuthenticated && bookingsData?.bookingsByClientId
        ? bookingsData.bookingsByClientId.map((booking: any) => booking.id)
        : []
    );
    
    // Add booking events with more lenient filtering
    const clientBookingEvents = (isAuthenticated && bookingsData?.bookingsByClientId) 
      ? bookingsData.bookingsByClientId
          .map((booking: any) => {
            console.log("Processing client booking for calendar:", booking.id, "practitionerId:", booking.practitionerId);
            
            // Skip bookings without required time fields
            if (!booking.startTime || !booking.endTime) {
              console.log(`Skipping booking ${booking.id} - missing time info`);
              return null;
            }
            
            // Be more lenient with practitioner ID check - log but still display
            if (booking.practitionerId !== practitionerId) {
              console.log(`Note: Booking ${booking.id} practitioner ID (${booking.practitionerId}) doesn't match URL param (${practitionerId})`);
            }
            
            return {
              id: `booking-${booking.id}`,
              start: booking.startTime,
              end: booking.endTime,
              title: `Booked: ${booking.sessionType?.name || "Session"}`,
              backgroundColor: getBookingColor(booking.status),
              borderColor: getBookingBorderColor(booking.status),
              textColor: "#ffffff",
              extendedProps: {
                bookingId: booking.id,
                status: booking.status,
                paymentStatus: booking.payment?.status,
                isBooking: true,
                isClientBooking: true,
                sessionType: booking.sessionType?.name,
                sessionFormat: booking.sessionType?.format
              }
            };
          })
          .filter(Boolean) // Remove null entries
      : [];
    
    // Add other clients' bookings as gray events - with extensive debugging
    const otherClientBookingEvents = practitionerBookingsData?.getBookingsByPractitionerId
      ? practitionerBookingsData.getBookingsByPractitionerId
          .map((booking: any) => {
            // Log the raw booking data to see the structure
            console.log("Processing a practitioner booking - raw data:", JSON.stringify(booking));
            
            // Skip if this is the current client's booking (already shown above)
            if (isAuthenticated && clientBookingIds.has(booking.id)) {
              console.log(`Skipping booking ${booking.id} - this is the current client's booking`);
              return null;
            }
            
            // Skip bookings without required time fields
            if (!booking.startTime || !booking.endTime) {
              console.log(`Skipping booking ${booking.id} - missing time info`);
              return null;
            }
            
            // Since we're already in the practitioner's bookings list, we don't need to check practitionerId
            // The backend query should have already filtered by practitioner ID
            
            console.log("Creating other client booking event:", {
              id: booking.id,
              startTime: booking.startTime,
              endTime: booking.endTime,
              status: booking.status,
              clientId: booking.client?.id,
              practitionerId: booking.practitionerId,
              urlPractitionerId: practitionerId,
              idsMatch: booking.practitionerId === practitionerId
            });
            
            return {
              id: `other-booking-${booking.id}`,
              start: booking.startTime,
              end: booking.endTime,
              title: "Unavailable",
              backgroundColor: "rgba(160, 160, 160, 0.85)", // More opaque gray
              borderColor: "#757575",
              textColor: "#ffffff",
              display: "block", // Force display as blocks, not background
              editable: false,
              classNames: ["other-client-booking"],
              extendedProps: {
                isOtherClientBooking: true,
                isBooking: false, // Don't treat as a bookable/clickable event
                status: booking.status
              }
            };
          })
          .filter(Boolean)
      : [];
      
    console.log("Final events for calendar:", {
      availabilityEvents: availabilityEvents.length,
      clientBookingEvents: clientBookingEvents.length,
      otherClientBookingEvents: otherClientBookingEvents?.length || 0,
      total: availabilityEvents.length + clientBookingEvents.length + (otherClientBookingEvents?.length || 0)
    });
    
    // Order is important - put other client bookings AFTER availability but BEFORE client's own bookings
    // This ensures they show on top of the availability background
    return [...availabilityEvents, ...(otherClientBookingEvents || []), ...clientBookingEvents];
  }, [availabilityData, bookingsData, practitionerBookingsData, isAuthenticated, practitionerId, user]);

  // Add CSS for other client bookings

  // Calendar ref for navigation
  const calendarRef = createRef<any>();
  
  // Toggle between 24-hour and business hours view
  const toggleTimeView = () => {
    setIs24HourView(prev => !prev);
  };
  
  // Navigation handlers
  const handlePrevClick = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.prev();
    }
  };

  const handleNextClick = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.next();
    }
  };

  // Handle booking a session
  const handleBookSession = async () => {
    if (!selectedSlot || !selectedSessionType || !user?.id || !practitionerId) return;

    try {
      // Use the selected slot times that were calculated when selecting the time
      const sessionStart = selectedSlot.start;
      const sessionEnd = selectedSlot.end;

      // Get the base URL without query parameters for SMS notifications
      const baseUrl = window.location.origin + window.location.pathname;

      await createBooking({
        variables: {
          input: {
            clientId: user.id,
            practitionerId: practitionerId,
            startTime: sessionStart.toISOString(),
            endTime: sessionEnd.toISOString(),
            sessionTypeId: selectedSessionType.id,
            brand: brandConfig.brandIdentifier,
            payment: {
              method: BookingPaymentMethod.STRIPE,
              amount: selectedSessionType.price,
              currency: "USD"
            },
            sendReminders: true,
            returnUrl: baseUrl // Include base URL without query params for SMS link
          }
        }
      });
    } catch (error) {
      console.error("Error booking session:", error);
      toast({
        title: "Error",
        description: "Failed to book the session. Please try again.",
        status: "error",
        duration: 3000
      });
    }
  };

  // Show error if no practitioner ID is provided
  React.useEffect(() => {
    if (!practitionerId) {
      toast({
        title: "Practitioner ID Missing",
        description: "No practitioner ID was provided in the URL.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [practitionerId, toast]);

  // Add a new state variable for post-login loading
  const [isPostLoginLoading, setIsPostLoginLoading] = useState(false);

  // Loading state
  if (authLoading) {
    return (
      <>
        <NavbarWithCallToAction />
        <Container maxW="container.xl" py={8}>
          <VStack spacing={4} align="center" justify="center" height="400px">
            <Text>Loading...</Text>
          </VStack>
        </Container>
        <FooterWithFourColumns />
      </>
    );
  }

  return (
    <>
      <NavbarWithCallToAction />
      <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 2, md: 4 }}>
        <VStack spacing={{ base: 5, md: 8 }} align="stretch">
          {/* Practitioner Info Header */}
          <Box 
            bg="blue.100" 
            p={{ base: 5, md: 8 }} 
            borderRadius="lg" 
            boxShadow="md"
            textAlign="center"
            borderWidth="1px"
            borderColor="blue.200"
          >
            {practitionerLoading ? (
              <Heading size="lg" color="blue.700">Loading practitioner details...</Heading>
            ) : practitionerData?.practionerById ? (
              <>
                <Heading size="xl" mb={3} color="blue.700">
                  Book a Session with {practitionerData.practionerById.fName} {practitionerData.practionerById.lName}
                </Heading>
                <Text fontSize={{ base: "md", md: "lg" }} color="blue.600" fontWeight="medium">
                  Select a session type below, then choose an available time slot on the calendar.
                </Text>
              </>
            ) : (
              <Heading size="lg" color="blue.700">Book a Session with Practitioner</Heading>
            )}
          </Box>

          {/* Session Type Selection */}
          <Box>
            <Heading size="md" mb={4}>
              <Badge colorScheme="blue" mr={2} fontSize="md" px={2}>Step 1</Badge>
              Choose a Session Type
            </Heading>
            {sessionTypesLoading ? (
              <Text>Loading session types...</Text>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {sessionTypesData?.sessionTypesByPractitioner
                  .filter((type: any) => type.availableOnCalendar)
                  .map((sessionType: any) => (
                    <Card 
                      key={sessionType.id} 
                      borderWidth="2px"
                      borderRadius="lg"
                      overflow="hidden"
                      cursor="pointer"
                      boxShadow="md"
                      _hover={{ 
                        transform: "translateY(-8px)",
                        boxShadow: "xl" 
                      }}
                      bg={selectedSessionType?.id === sessionType.id ? "green.50" : "white"}
                      borderColor={selectedSessionType?.id === sessionType.id ? "green.500" : "gray.200"}
                      transition="all 0.3s ease"
                      onClick={() => setSelectedSessionType(sessionType)}
                      height="100%"
                      display="flex"
                      flexDirection="column"
                    >
                      <Box 
                        h="12px" 
                        bg={sessionType.color || "#4A7C59"} 
                        w="100%" 
                      />
                      <CardHeader pb={2} textAlign="center">
                        <Badge colorScheme={sessionType.format === "INDIVIDUAL" ? "green" : "blue"} mb={2}>
                          {sessionType.format}
                        </Badge>
                        <Heading 
                          size="md" 
                          textAlign="center"
                          color={selectedSessionType?.id === sessionType.id ? "green.700" : "gray.700"}
                          fontWeight="bold"
                        >
                          {sessionType.name}
                        </Heading>
                      </CardHeader>
                      <CardBody pt={0} pb={2} flexGrow={1}>
                        {sessionType.description && (
                          <Text fontSize="sm" color="gray.600" noOfLines={2} mb={3} textAlign="center">
                            {sessionType.description}
                          </Text>
                        )}
                        <HStack justify="center" mb={2}>
                          <Badge colorScheme="purple" fontSize="md" px={2} py={1}>{sessionType.duration} min</Badge>
                          <Badge colorScheme="orange" fontSize="md" px={2} py={1}>${sessionType.price}</Badge>
                        </HStack>
                      </CardBody>
                      <CardFooter pt={0} bg={selectedSessionType?.id === sessionType.id ? "green.100" : "gray.50"} mt="auto">
                        <Flex width="100%" justify="space-between" align="center">
                          {selectedSessionType?.id === sessionType.id ? (
                            <Badge colorScheme="green" px={3} py={1} borderRadius="full" fontSize="sm" ml="auto" mr="auto">
                              <Flex align="center">
                                <Icon as={CheckIcon} mr={1} boxSize={3} />
                                Selected
                              </Flex>
                            </Badge>
                          ) : (
                            <Box 
                              fontSize="sm" 
                              color="blue.600" 
                              fontWeight="medium" 
                              ml="auto" 
                              mr="auto"
                              textDecoration="underline"
                            >
                              Click to select this session
                            </Box>
                          )}
                        </Flex>
                      </CardFooter>
                    </Card>
                  ))}
              </SimpleGrid>
            )}
          </Box>

          {/* Availability Calendar */}
          {selectedSessionType ? (
            <Box>
              <Heading size="md" mb={4}>
                <Badge colorScheme="blue" mr={2} fontSize="md" px={2}>Step 2</Badge>
                Available Time Slots {availabilityLoading ? "(Loading...)" : ""}
              </Heading>
              
              <HStack spacing={4} flexWrap="wrap" justify="space-between" pt={2} pb={4}>
                <Box 
                  p={3} 
                  bg="green.50" 
                  borderRadius="md"
                  borderLeft="4px solid" 
                  borderColor="yellow.400"
                  boxShadow="sm"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "md" }}
                  transition="all 0.3s"
                  animation={`${glowAnimation} 4s infinite ease-in-out`}
                >
                  <Heading size="xs" mb={1} color="green.700">Booking Guide</Heading>
                  <Text fontSize="xs" color="gray.600">
                    Click anywhere in a green area to book your session.
                  </Text>
                </Box>

                <Button
                  onClick={toggleTimeView}
                  colorScheme={is24HourView ? "blue" : "gray"}
                  size="sm"
                  variant={is24HourView ? "solid" : "outline"}
                  leftIcon={<Icon as={TimeIcon} />}
                  transition="all 0.3s ease"
                  boxShadow={is24HourView ? "md" : "none"}
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "md"
                  }}
                >
                  {is24HourView ? "Show Business Hours" : "Show Full 24 Hours"}
                </Button>
              </HStack>

              <Box 
                p={{ base: 2, md: 4 }} 
                borderWidth="1px" 
                borderRadius="lg"
                overflowX="auto"
                position="relative"
                boxShadow="lg"
                _before={{
                  content: "\"\"",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: "lg",
                  padding: "2px",
                  background: "linear-gradient(45deg, #3182CE, #38A169, #ECC94B)",
                  mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                  maskComposite: "xor",
                  pointerEvents: "none"
                }}
              >
                {/* Navigation buttons */}
                <Box
                  position="absolute"
                  left={{ base: 1, md: 2 }}
                  top="50%"
                  transform="translateY(-50%)"
                  zIndex={10}
                  display="flex"
                  flexDirection="column"
                  gap={2}
                >
                  <Button
                    onClick={handlePrevClick}
                    size="sm"
                    borderRadius="full"
                    width={{ base: "36px", md: "40px" }}
                    height={{ base: "36px", md: "40px" }}
                    p={0}
                    bg="green.400"
                    _hover={{ 
                    bg: "green.500", 
                    transform: "scale(1.1) translateX(-2px)", 
                    boxShadow: "0 0 15px rgba(57, 161, 105, 0.6)" 
                  }}
                  _active={{ bg: "green.600", transform: "scale(0.95)" }}
                  transition="all 0.3s ease"
                  boxShadow="0 2px 10px rgba(0,0,0,0.15)"
                  aria-label="Previous"
                >
                  <ChevronLeftIcon boxSize={{ base: 4, md: 6 }} color="white" />
                </Button>
              </Box>

              <Box
                position="absolute"
                right={{ base: 1, md: 2 }}
                top="50%"
                transform="translateY(-50%)"
                zIndex={10}
                display="flex"
                flexDirection="column"
                gap={2}
              >
                <Button
                  onClick={handleNextClick}
                  size="sm"
                  borderRadius="full"
                  width={{ base: "36px", md: "40px" }}
                  height={{ base: "36px", md: "40px" }}
                  p={0}
                  bg="green.400"
                  _hover={{ 
                    bg: "green.500", 
                    transform: "scale(1.1) translateX(2px)", 
                    boxShadow: "0 0 15px rgba(57, 161, 105, 0.6)" 
                  }}
                  _active={{ bg: "green.600", transform: "scale(0.95)" }}
                  transition="all 0.3s ease"
                  boxShadow="0 2px 10px rgba(0,0,0,0.15)"
                  aria-label="Next"
                >
                  <ChevronRightIcon boxSize={{ base: 4, md: 6 }} color="white" />
                </Button>
              </Box>
              
              {/* Calendar CSS */}
              <style>
                {`
                  .fc-event {
                    cursor: pointer;
                    transition: all 0.2s ease;
                    overflow: hidden;
                  }
                  .fc-event:hover {
                    transform: scale(1.02);
                    box-shadow: 0 5px 15px rgba(0,0,0,0.2) !important;
                    z-index: 100 !important;
                  }
                  .fc-event-title {
                    font-weight: bold;
                  }
                  
                  /* Set proper z-index hierarchy for different event types */
                  .fc-timegrid-event-harness {
                    z-index: 5 !important;
                  }
                  .fc-timegrid-event {
                    z-index: 15 !important;
                  }
                  .fc-timegrid-bg {
                    z-index: 1 !important;
                  }
                  
                  /* Other client bookings need to stand out */
                  .other-client-booking {
                    z-index: 25 !important; /* Higher than regular events */
                    opacity: 1 !important;
                    border-radius: 4px !important;
                    box-shadow: 0 3px 10px rgba(0,0,0,0.3) !important;
                    border-left: 4px solid #757575 !important;
                    font-weight: bold !important;
                    pointer-events: none !important; /* Make them not clickable */
                    background-color: rgba(160, 160, 160, 0.9) !important; 
                    display: block !important;
                  }
                  
                  /* Session events styling */
                  .exciting-session-event {
                    border-radius: 6px !important;
                    border-left: 4px solid #F6E05E !important;
                    box-shadow: 0 3px 10px rgba(0,0,0,0.15) !important;
                  }
                  
                  ${is24HourView ? `
                  /* 24-hour specific styles */
                  .fc-timegrid-slot {
                    height: 25px !important;
                    min-height: 25px !important;
                  }
                  .fc-timegrid-slot-lane {
                    height: 25px !important;
                  }
                  .fc-timegrid-col.fc-day {
                    min-height: 600px !important;
                  }
                  /* Night hours styling */
                  .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="00:"],
                  .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="01:"],
                  .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="02:"],
                  .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="03:"],
                  .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="04:"],
                  .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="05:"],
                  .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="06:"],
                  .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="07:"],
                  .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="22:"],
                  .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="23:"] {
                    background-color: rgba(0, 0, 0, 0.03);
                  }
                  ` : ""}
                  
                  /* Current time indicator */
                  .fc-timegrid-now-indicator-line {
                    border-color: #E53E3E !important;
                    border-width: 2px !important;
                  }
                  .fc-timegrid-now-indicator-arrow {
                    border-color: #E53E3E !important;
                    border-width: 5px !important;
                  }
                  
                  /* Business hours highlight */
                  .fc-non-business {
                    background: rgba(0, 0, 0, 0.025);
                  }
                  
                  /* Mobile adjustments */
                  @media (max-width: 768px) {
                    .fc-event {
                      font-size: 0.7em;
                    }
                    /* Fix week view title on mobile */
                    .fc-timeGridWeek-view .fc-toolbar-title {
                      font-size: 0.9rem !important; 
                      white-space: nowrap !important;
                      overflow: hidden !important;
                      text-overflow: ellipsis !important;
                    }
                  }
                  
                  /* Make non-available areas have a grayed out background */
                  /* Make non-available areas have a grayed out background */
                  .fc-timegrid-col-bg {
                    background-color: rgba(0, 0, 0, 0.05);
                  }
                  
                  /* Make available areas stand out better */
                  .fc-bg-event {
                    opacity: 1 !important;
                  }
                  
                  /* Add a more visible pattern to available slots */
                  .fc-bg-event {
                    background-image: repeating-linear-gradient(
                      45deg,
                      rgba(56, 161, 105, 0.2),
                      rgba(56, 161, 105, 0.2) 10px,
                      rgba(56, 161, 105, 0.3) 10px,
                      rgba(56, 161, 105, 0.3) 20px
                    ) !important;
                  }
                `}
              </style>
              
              <FullCalendar
                ref={calendarRef}
                plugins={[timeGridPlugin, interactionPlugin, dayGridPlugin]}
                initialView="timeGridWeek"
                initialDate={initialDate}
                datesSet={handleDatesSet}
                events={events}
                
                headerToolbar={{
                  left: "prev,next",
                  center: "title",
                  right: "dayGridMonth,timeGridWeek,timeGridDay"
                }}

                businessHours={{
                  daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
                  startTime: "08:00",
                  endTime: "22:00"
                }}
                
                height={is24HourView ? "1400px" : "auto"}
                expandRows={true}
                stickyHeaderDates={true}
                
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                eventContent={renderEventContent}
                
                views={{
                  timeGridDay: {
                    titleFormat: { month: "short", day: "numeric" },
                    dayHeaderFormat: { weekday: "long" },
                    slotDuration: "00:30:00",
                    slotLabelInterval: "01:00:00"
                  },
                  timeGridWeek: {
                    titleFormat: { month: "short", year: "numeric" },
                    dayHeaderFormat: { weekday: "short", day: "numeric" },
                    slotDuration: "01:00:00",
                    slotLabelInterval: "01:00:00"
                  },
                  dayGridMonth: {
                    dayHeaderFormat: { weekday: "narrow" }
                  }
                }}
                
                slotLabelFormat={{
                  hour: "numeric",
                  minute: "2-digit",
                  omitZeroMinute: true,
                  meridiem: "short"
                }}
                
                slotMinTime={is24HourView ? "00:00:00" : "08:00:00"}
                slotMaxTime={is24HourView ? "24:00:00" : "22:00:00"}
                allDaySlot={false}
                nowIndicator={true}
                selectable={true}
                selectMirror={true}
                unselectAuto={true}
              />
            </Box>
          </Box>
          ) : (
            <Box 
              textAlign="center" 
              p={8} 
              borderWidth="1px" 
              borderRadius="lg" 
              borderStyle="dashed"
              borderColor="blue.300"
              bg="blue.50"
            >
              <Icon as={InfoIcon} boxSize={10} color="blue.400" mb={4} />
              <Heading size="md" mb={2} color="blue.700">
                Please Select a Session Type First
              </Heading>
              <Text color="blue.600">
                Choose a session type above to see the available time slots.
              </Text>
            </Box>
          )}

          {/* Add "Your Bookings" section */}
          <Box mt={{ base: 4, md: 6 }}>
            <Heading size={{ base: "md", md: "lg" }} mb={2}>
              Your Bookings
            </Heading>
            <Text fontSize="sm" color="gray.600" mb={4}>
              These are your personal bookings. Only sessions you've booked will appear here.
            </Text>
            
            {!isAuthenticated ? (
              <Box 
                p={{ base: 4, md: 6 }} 
                borderWidth="1px" 
                borderRadius="md" 
                bg="blue.50"
                textAlign="center"
              >
                <VStack spacing={4}>
                  <Icon as={LockIcon} w={10} h={10} color="blue.500" />
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    Please log in to view and manage your bookings
                  </Text>
                  <Button
                    colorScheme="blue"
                    onClick={onLoginModalOpen}
                    leftIcon={<LockIcon />}
                    size={{ base: "md", md: "lg" }}
                    px={8}
                    _hover={{
                      transform: "translateY(-2px)",
                      boxShadow: "md"
                    }}
                    transition="all 0.2s"
                  >
                    Log In
                  </Button>
                </VStack>
              </Box>
            ) : bookingsLoading ? (
              <Text fontSize={{ base: "sm", md: "md" }}>Loading your bookings...</Text>
            ) : !bookingsData?.bookingsByClientId?.length ? (
              <Text fontSize={{ base: "sm", md: "md" }}>You don't have any bookings yet.</Text>
            ) : (
              <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                {/* Upcoming Bookings */}
                <Box>
                  <Heading size="sm" mb={3} color="green.600">
                    <Badge colorScheme="green" mr={2}>Upcoming</Badge>
                    Upcoming Bookings
                  </Heading>
                  <VStack spacing={{ base: 2, md: 3 }} align="stretch">
                    {bookingsData.bookingsByClientId
                      .filter((booking: any) => new Date(booking.startTime) > new Date())
                      .sort((a: any, b: any) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
                      .map((booking: any) => {
                        const isPending = booking.status === "PENDING";
                        const StatusIcon = getStatusIcon(booking.status);
                        
                        return (
                          <Box 
                            key={booking.id} 
                            p={{ base: 3, md: 4 }} 
                            borderWidth="1px" 
                            borderRadius="md"
                            borderLeftWidth="4px"
                            borderLeftColor={getBookingColor(booking.status)}
                            bg="white"
                            fontSize={{ base: "sm", md: "md" }}
                            position="relative"
                            transition="all 0.2s"
                            _hover={{
                              boxShadow: "md",
                              transform: "translateY(-2px)"
                            }}
                            cursor="pointer"
                            onClick={() => {
                              setSelectedBooking(booking);
                              onBookingDetailOpen();
                            }}
                            animation={isPending ? `${pulseAnimation} 3s infinite` : undefined}
                          >
                            {/* Show ID for debugging */}
                            <Text fontSize="xs" position="absolute" top={1} right={2} color="gray.400">
                              ID: {booking.id.substring(0, 8)}...
                            </Text>
                            
                            {isPending && (
                              <Badge 
                                position="absolute" 
                                top={2} 
                                right={2}
                                colorScheme="yellow"
                                variant="solid"
                                px={2}
                                py={1}
                                borderRadius="full"
                                display="flex"
                                alignItems="center"
                                gap={1}
                              >
                                <Icon as={TimeIcon} boxSize={3} />
                                Pending
                              </Badge>
                            )}
                            
                            <Heading size={{ base: "xs", md: "sm" }}>{booking.sessionType?.name || "Booking"}</Heading>
                            <Text>
                              Date: {new Date(booking.startTime).toLocaleDateString()}
                            </Text>
                            <Text>
                              Time: {new Date(booking.startTime).toLocaleTimeString([], {hour: "2-digit", minute:"2-digit"})} - {new Date(booking.endTime).toLocaleTimeString([], {hour: "2-digit", minute:"2-digit"})}
                            </Text>
                            <HStack mt={2}>
                              <Icon 
                                as={StatusIcon} 
                                color={
                                  booking.status === "CONFIRMED" ? "blue.500" : 
                                  booking.status === "COMPLETED" ? "green.500" :
                                  booking.status === "CANCELLED" ? "red.500" : "yellow.500"
                                }
                              />
                              <Text>Status:</Text> 
                              <Badge colorScheme={
                                booking.status === "CONFIRMED" ? "blue" : 
                                booking.status === "COMPLETED" ? "green" :
                                booking.status === "CANCELLED" ? "red" : "yellow"
                              }>{booking.status}</Badge>
                            </HStack>
                            <HStack mt={1}>
                              <Text>Payment:</Text>
                              <Badge colorScheme={
                                booking.payment?.status === "COMPLETED" ? "green" : 
                                booking.payment?.status === "PENDING" ? "yellow" : "red"
                              }>{booking.payment?.status}</Badge>
                            </HStack>
                            
                            <Button
                              as={Link}
                              to={`/bookings/${booking.id}`}
                              size="sm"
                              colorScheme="blue"
                              mt={3}
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent the box onClick from triggering
                              }}
                              rightIcon={<ExternalLinkIcon />}
                            >
                              View Details
                            </Button>
                          </Box>
                        );
                      })}
                    {bookingsData.bookingsByClientId.filter((booking: any) => 
                      new Date(booking.startTime) > new Date()).length === 0 && (
                      <Text fontSize="sm" color="gray.500" fontStyle="italic">No upcoming bookings</Text>
                    )}
                  </VStack>
                </Box>
                
                {/* Past Bookings */}
                <Box>
                  <Heading size="sm" mb={3} color="gray.600">
                    <Badge colorScheme="gray" mr={2}>Past</Badge>
                    Past Bookings
                  </Heading>
                  <VStack spacing={{ base: 2, md: 3 }} align="stretch">
                    {bookingsData.bookingsByClientId
                      .filter((booking: any) => new Date(booking.startTime) <= new Date())
                      .sort((a: any, b: any) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()) // Most recent first
                      .map((booking: any) => {
                        const StatusIcon = getStatusIcon(booking.status);
                        
                        return (
                          <Box 
                            key={booking.id} 
                            p={{ base: 3, md: 4 }} 
                            borderWidth="1px" 
                            borderRadius="md"
                            borderLeftWidth="4px"
                            borderLeftColor={getBookingColor(booking.status)}
                            bg="gray.50"
                            fontSize={{ base: "sm", md: "md" }}
                            position="relative"
                            transition="all 0.2s"
                            opacity={0.9}
                            _hover={{
                              boxShadow: "md",
                              opacity: 1
                            }}
                            cursor="pointer"
                            onClick={() => {
                              setSelectedBooking(booking);
                              onBookingDetailOpen();
                            }}
                          >
                            <Heading size={{ base: "xs", md: "sm" }}>{booking.sessionType?.name || "Booking"}</Heading>
                            <Text>
                              Date: {new Date(booking.startTime).toLocaleDateString()}
                            </Text>
                            <Text>
                              Time: {new Date(booking.startTime).toLocaleTimeString([], {hour: "2-digit", minute:"2-digit"})} - {new Date(booking.endTime).toLocaleTimeString([], {hour: "2-digit", minute:"2-digit"})}
                            </Text>
                            <HStack mt={2}>
                              <Icon 
                                as={StatusIcon} 
                                color={
                                  booking.status === "CONFIRMED" ? "blue.500" : 
                                  booking.status === "COMPLETED" ? "green.500" :
                                  booking.status === "CANCELLED" ? "red.500" : "yellow.500"
                                }
                              />
                              <Text>Status:</Text> 
                              <Badge colorScheme={
                                booking.status === "CONFIRMED" ? "blue" : 
                                booking.status === "COMPLETED" ? "green" :
                                booking.status === "CANCELLED" ? "red" : "yellow"
                              }>{booking.status}</Badge>
                            </HStack>
                            
                            <Button
                              as={Link}
                              to={`/bookings/${booking.id}`}
                              size="sm"
                              colorScheme="gray"
                              mt={3}
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent the box onClick from triggering
                              }}
                              rightIcon={<ExternalLinkIcon />}
                            >
                              View Details
                            </Button>
                          </Box>
                        );
                      })}
                    {bookingsData.bookingsByClientId.filter((booking: any) => 
                      new Date(booking.startTime) <= new Date()).length === 0 && (
                      <Text fontSize="sm" color="gray.500" fontStyle="italic">No past bookings</Text>
                    )}
                  </VStack>
                </Box>
              </VStack>
            )}
          </Box>

          {/* Booking instructions */}
          <Box p={5} bg="blue.50" borderRadius="md" boxShadow="sm">
            <Heading size="sm" mb={3}>How to Book a Session</Heading>
            <VStack align="start" spacing={2}>
              <HStack align="start">
                <Box as="span" fontWeight="bold" minWidth="30px">1.</Box>
                <Text>Select your preferred session type from the options above.</Text>
              </HStack>
              <HStack align="start">
                <Box as="span" fontWeight="bold" minWidth="30px">2.</Box>
                <Text>Click on an available time slot on the calendar (shown in green).</Text>
              </HStack>
              <HStack align="start">
                <Box as="span" fontWeight="bold" minWidth="30px">3.</Box>
                <Text>Confirm your booking in the dialog that appears.</Text>
              </HStack>
              <HStack align="start">
                <Box as="span" fontWeight="bold" minWidth="30px">4.</Box>
                <Text>You'll receive an SMS confirmation after booking is complete.</Text>
              </HStack>
            </VStack>
          </Box>
        </VStack>
      </Container>

      {/* Booking Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size={{ base: "sm", md: "md" }}>
        <ModalOverlay 
          bg="rgba(0,0,0,0.7)"
          backdropFilter="blur(6px)"
        />
        <ModalContent 
          mx={{ base: 3, md: 0 }}
          borderRadius="xl"
          overflow="hidden"
          boxShadow="0 10px 30px -5px rgba(0, 0, 0, 0.3)"
          bg="white"
          position="relative"
          _before={{
            content: "\"\"",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: "linear-gradient(90deg, #38A169, #48BB78)",
            zIndex: 0
          }}
        >
          <ModalHeader 
            fontSize={{ base: "lg", md: "xl" }}
            py={4}
            bg="green.50"
            color="green.700"
          >
            Confirm Your Booking
          </ModalHeader>
          <ModalBody fontSize={{ base: "sm", md: "md" }} py={6}>
            {selectedSlot && selectedSessionType && (
              <VStack spacing={4} align="stretch">
                <Box p={4} bg="green.50" borderRadius="md">
                  <Heading size="sm" mb={2}>{selectedSessionType.name}</Heading>
                  <Text fontWeight="bold">Date & Time:</Text>
                  <Text>
                    {selectedSlot.start.toLocaleDateString()} at{" "}
                    {selectedSlot.start.toLocaleTimeString([], {hour: "2-digit", minute:"2-digit"})}
                  </Text>
                  
                  <HStack mt={2} spacing={4}>
                    <Box>
                      <Text fontSize="xs" color="gray.500">Duration</Text>
                      <Badge colorScheme="purple">{selectedSessionType.duration} min</Badge>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">Price</Text>
                      <Badge colorScheme="orange">${selectedSessionType.price}</Badge>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">Format</Text>
                      <Badge colorScheme={selectedSessionType.format === "INDIVIDUAL" ? "green" : "blue"}>
                        {selectedSessionType.format}
                      </Badge>
                    </Box>
                  </HStack>
                </Box>
                
                <Box p={4} borderWidth="1px" borderRadius="md" borderColor="blue.200" bg="blue.50">
                  <Text fontSize="sm">
                    By confirming, you agree to book this session. Payment will be processed according to the practitioner's policies.
                  </Text>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button 
              colorScheme="green" 
              mr={3} 
              onClick={handleBookSession}
              isLoading={bookingLoading}
              loadingText="Booking..."
              size={{ base: "sm", md: "md" }}
              flex={{ base: 1, md: "unset" }}
              boxShadow="md"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "lg",
                bg: "green.500"
              }}
              transition="all 0.2s"
            >
              Confirm Booking
            </Button>
            <Button 
              variant="outline" 
              colorScheme="gray"
              onClick={onClose}
              size={{ base: "sm", md: "md" }}
              flex={{ base: 1, md: "unset" }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Booking Details Modal */}
      <Modal isOpen={isBookingDetailOpen} onClose={onBookingDetailClose} size={{ base: "sm", md: "md" }}>
        <ModalOverlay 
          bg="rgba(0,0,0,0.7)"
          backdropFilter="blur(6px)"
        />
        <ModalContent 
          mx={{ base: 3, md: 0 }}
          borderRadius="xl"
          overflow="hidden"
          boxShadow="0 10px 30px -5px rgba(0, 0, 0, 0.3)"
          bg="white"
          position="relative"
          _before={{
            content: "\"\"",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: "linear-gradient(90deg, #3182CE, #48BB78)",
            zIndex: 0
          }}
        >
          <ModalHeader fontSize={{ base: "lg", md: "xl" }}>Booking Details</ModalHeader>
          <ModalBody fontSize={{ base: "sm", md: "md" }}>
            {selectedBooking && (
              <VStack spacing={4} align="stretch">
                <Heading size="md">{selectedBooking.sessionType?.name || "Booking"}</Heading>
                
                <Box p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                  <Text fontWeight="bold">Date & Time:</Text>
                  <Text>
                    {new Date(selectedBooking.startTime).toLocaleDateString()} at{" "}
                    {new Date(selectedBooking.startTime).toLocaleTimeString([], {hour: "2-digit", minute:"2-digit"})} - {" "}
                    {new Date(selectedBooking.endTime).toLocaleTimeString([], {hour: "2-digit", minute:"2-digit"})}
                  </Text>
                </Box>
                
                <Box p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                  <Text fontWeight="bold">Status:</Text>
                  <Badge colorScheme={
                    selectedBooking.status === "CONFIRMED" ? "blue" : 
                    selectedBooking.status === "COMPLETED" ? "green" :
                    selectedBooking.status === "CANCELLED" ? "red" : "yellow"
                  } fontSize="0.9em" px={2} py={1}>
                    {selectedBooking.status}
                  </Badge>
                </Box>
                
                {selectedBooking.payment && (
                  <Box p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                    <Text fontWeight="bold">Payment:</Text>
                    <Text>Method: {selectedBooking.payment.method}</Text>
                    <Text>Amount: {selectedBooking.payment.amount} {selectedBooking.payment.currency}</Text>
                    <Text>Status: 
                      <Badge ml={2} colorScheme={
                        selectedBooking.payment.status === "COMPLETED" ? "green" : 
                        selectedBooking.payment.status === "PENDING" ? "yellow" : "red"
                      }>
                        {selectedBooking.payment.status}
                      </Badge>
                    </Text>
                  </Box>
                )}
                
                {selectedBooking.sessionType && (
                  <Box p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                    <Text fontWeight="bold">Session Type:</Text>
                    <Text>{selectedBooking.sessionType.name} ({selectedBooking.sessionType.format})</Text>
                    <Text>Duration: {selectedBooking.sessionType.duration} minutes</Text>
                    {selectedBooking.sessionType.price > 0 && (
                      <Text>Price: ${selectedBooking.sessionType.price}</Text>
                    )}
                  </Box>
                )}
                
                {selectedBooking.status !== "CANCELLED" && selectedBooking.status !== "COMPLETED" && (
                  <Box mt={4}>
                    <Heading size="sm" mb={2}>Cancel Booking</Heading>
                    <FormControl isRequired>
                      <FormLabel>Reason for cancellation</FormLabel>
                      <Input 
                        value={cancellationReason}
                        onChange={(e) => setCancellationReason(e.target.value)}
                        placeholder="Please provide a reason"
                      />
                    </FormControl>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            {selectedBooking && selectedBooking.status !== "CANCELLED" && selectedBooking.status !== "COMPLETED" && (
              <Button 
                colorScheme="red" 
                mr={3} 
                onClick={handleCancelBooking}
                isLoading={cancelLoading}
                isDisabled={!cancellationReason.trim()}
                size={{ base: "sm", md: "md" }}
                flex={{ base: 1, md: "unset" }}
              >
                Cancel Booking
              </Button>
            )}
            <Button 
              variant="ghost" 
              onClick={onBookingDetailClose}
              size={{ base: "sm", md: "md" }}
              flex={{ base: 1, md: "unset" }}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Login Modal */}
      <LoginWithSmsModal 
        isOpen={isLoginModalOpen} 
        onClose={onLoginModalClose} 
        onLoginSuccess={() => {
          // Don't close login modal immediately, show loading state first
          setIsPostLoginLoading(true);

          // If there's a pending slot selection, use it
          if (pendingSlotSelection && selectedSessionType) {
            // Longer timeout to ensure auth context and client data are updated
            setTimeout(() => {
              setIsPostLoginLoading(false);
              onLoginModalClose();
              refreshAuth(); // Refresh auth to get latest user data
              setSelectedSlot(pendingSlotSelection);
              onOpen(); // Open the booking modal
              setPendingSlotSelection(null); // Clear the pending selection
            }, 3000); // Increase to 3 seconds
          } else {
            // If no pending selection, just refresh the page after a delay
            setTimeout(() => {
              setIsPostLoginLoading(false);
              onLoginModalClose();
              refreshAuth(); // Refresh auth to get latest user data
            }, 4000);
          }
        }}
      />

      {/* User Details Modal */}
      {user && (
        <CaptureUserDetailsModal
          isOpen={isUserDetailsModalOpen}
          onClose={onUserDetailsModalClose}
          userId={user.id}
          currentUserData={{
            fName: user.fName,
            lName: user.lName,
            email: user.email
          }}
          onUpdateSuccess={() => {
            refreshAuth(); // Refresh auth context to get updated user data
            toast({
              title: "Profile Updated",
              description: "Thank you for completing your profile",
              status: "success",
              duration: 3000
            });
          }}
        />
      )}

      {/* Add loading overlay for post-login state */}
      {isPostLoginLoading && (
        <Modal isOpen={true} onClose={() => {}} isCentered>
          <ModalOverlay backdropFilter="blur(10px)" />
          <ModalContent
            p={6}
            bg="white"
            borderRadius="xl"
            boxShadow="xl"
            textAlign="center"
            maxW="sm"
          >
            <VStack spacing={5}>
              <Heading size="md" color="green.600">
                Logging you in...
              </Heading>
              <Box position="relative" h="60px" w="full">
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  height="50px"
                  width="50px"
                  borderRadius="full"
                  border="3px solid"
                  borderColor="green.200"
                  borderTopColor="green.500"
                  animation={`${spinAnimation} 1s linear infinite`}
                />
              </Box>
              <Text fontSize="sm" color="gray.600">
                Please wait while we load your details
              </Text>
            </VStack>
          </ModalContent>
        </Modal>
      )}

      <FooterWithFourColumns />
    </>
  );
};
